import { TranslateService } from "@ngx-translate/core";
import { LoginResponse, OidcSecurityService } from "angular-auth-oidc-client";
import { firstValueFrom } from "rxjs";

import { ChangeDetectionStrategy, Component, inject } from "@angular/core";
import { Router, RouterOutlet } from "@angular/router";

import { AuthService } from "../services/authservice";
import { TokenService } from "../services/tokenservice";
import { OidcAuthenticationConfig } from "./auth/types";
import { DEFAULT_LANGUAGE, LANGUAGE_STORAGE_KEY, SUPPORTED_LANGUAGES } from "./constants";

@Component({
  selector: "app-root",
  templateUrl: "./app.component.html",
  styleUrl: "./app.component.scss",
  changeDetection: ChangeDetectionStrategy.Eager,
  imports: [RouterOutlet],
})
export class AppComponent {
  private readonly oidc = inject(OidcSecurityService);
  private readonly auth = inject(AuthService);
  private readonly router = inject(Router);
  private readonly token = inject(TokenService);
  private readonly translate = inject(TranslateService);

  constructor() {
    this.translate.addLangs(SUPPORTED_LANGUAGES);
    const browserLanguage = this.translate.getBrowserLang();
    const storedLanguage = localStorage.getItem(LANGUAGE_STORAGE_KEY);
    const useLanguage =
      [storedLanguage, browserLanguage].find((lang) => lang && SUPPORTED_LANGUAGES.includes(lang)) ?? DEFAULT_LANGUAGE;
    this.translate.setFallbackLang(DEFAULT_LANGUAGE);
    this.translate.use(useLanguage);
    this.handleLoginOrPassthrough();
  }

  private async handleLoginOrPassthrough() {
    const config = await firstValueFrom(this.oidc.getConfiguration(OidcAuthenticationConfig.Default));
    const secureRoutesLength = config?.secureRoutes?.length ?? 0;
    if (secureRoutesLength > 0) {
      // the secure routes are not added when oauthEnabled is false
      this.handleOidc();
    } else {
      this.auth.fetchUser();
    }
  }

  private async handleOidc() {
    const responses = await firstValueFrom(this.oidc.checkAuthMultiple());
    const authResponse = responses.find((response) => response.configId === OidcAuthenticationConfig.Default);
    const stepupResponse = responses.find((response) => response.configId === OidcAuthenticationConfig.SelfService);

    await this.token.setHttpToken();
    this.processOidcResponses(authResponse, stepupResponse);
  }

  private async processOidcResponses(
    authResponse: LoginResponse | undefined,
    stepupResponse: LoginResponse | undefined,
  ) {
    if (authResponse?.isAuthenticated) {
      this.auth.fetchUser();
    }

    if (stepupResponse?.isAuthenticated) {
      await this.handleOidcTokenPayload();
      const state = await firstValueFrom(this.oidc.getState(OidcAuthenticationConfig.SelfService));
      if (state) {
        this.clearStateAndRedirect(state);
      }

      setInterval(async () => this.handleOidcTokenPayload(), 30000);
    }
  }

  private clearStateAndRedirect(state: string) {
    // empty state to avoid redirecting to this page on every reload
    this.oidc.setState("", OidcAuthenticationConfig.SelfService).subscribe(() => this.handleRedirect(state));
  }

  private handleRedirect(state: string) {
    try {
      const { redirect } = JSON.parse(state) as { redirect?: string };
      if (redirect) this.router.navigateByUrl(redirect);
    } catch {
      console.log("No usable state found. Not redirecting.");
    }
  }

  private async handleOidcTokenPayload() {
    const token = await this.getOidcTokenPayload();
    if (token) this.auth.updateSelfserviceTokenStatus(token);
    else this.auth.clearSelfserviceTokenStatus();
  }

  private async getOidcTokenPayload() {
    return firstValueFrom(this.oidc.getPayloadFromIdToken(false, OidcAuthenticationConfig.SelfService));
  }
}
